import type { LongWindowNote, PatternAggregate } from '@kkp-fraud/shared';
import { formatWindow, parseThaiDay } from './format';
import { t } from './i18n';

/**
 * Wave 7 long windows (docs/specs/wave7/long-windows.md) — the console side. A pattern window of
 * at most 24 h is an exact sliding window, as before. A longer one, up to 366 days, is counted
 * by Bangkok calendar day: a whole number of days, meaning "the last N days including today",
 * and it counts only from the day the rule is released (no history before that).
 *
 * The engine alone decides what is valid (`window_too_long`, `window_whole_days`,
 * `window_long_aggregate`); this module only helps the editor say it before the engine does,
 * and reads the "since" facts the engine reports back. Everything here is pure.
 */

export const DAY_SECONDS = 86_400;
export const MAX_WINDOW_DAYS = 366;

/** A window counted by Bangkok day: longer than 24 h. */
export const isLongWindow = (seconds: number): boolean => Number.isFinite(seconds) && seconds > DAY_SECONDS;

/** Aggregates a long window cannot count — it keeps per-day totals, not the events themselves. */
export const LONG_OFF_AGGREGATES: ReadonlySet<PatternAggregate> = new Set(['last', 'seconds_since_last', 'streak']);

/**
 * The unit the window is picked in (the first of its two dropdowns), as its size in seconds: seconds, minutes, hours (a
 * window of at most 24 h, exact), then days, months (= 30 days) and years (= 365 days), which
 * are long windows counted by Bangkok day. Storage is always `windowSeconds`.
 */
export type WindowUnit = 1 | 60 | 3600 | 86400 | 2592000 | 31536000;
export const MONTH_SECONDS = 30 * DAY_SECONDS;
export const YEAR_SECONDS = 365 * DAY_SECONDS;
export const WINDOW_UNITS: readonly WindowUnit[] = [1, 60, 3600, 86400, 2592000, 31536000];
/** The catalogue label of each unit (the unit picker, and the input's own label). */
export const UNIT_LABEL: Readonly<Record<WindowUnit, string>> = {
  1: 'unit.second.label',
  60: 'unit.minute.label',
  3600: 'unit.hour.label',
  86400: 'unit.day.label',
  2592000: 'unit.month.label',
  31536000: 'unit.year.label',
};

/**
 * How many of each unit a window can be shown in: up to 24 h in seconds / minutes / hours,
 * 1–366 days, 1–12 months, 1 year. Only decides the unit a window is SHOWN in — what can be
 * picked is `WINDOW_CHOICES`.
 */
const UNIT_MAX: Readonly<Record<WindowUnit, number>> = {
  1: DAY_SECONDS,
  60: DAY_SECONDS / 60,
  3600: 24,
  86400: MAX_WINDOW_DAYS,
  2592000: 12,
  31536000: 1,
};

/** Window presets in seconds: the short ones, then 7 / 30 / 90 / 180 days and a year (365 days). */
export const WINDOW_PRESETS: readonly number[] = [300, 600, 900, 3600, 21600, 86400, ...[7, 30, 90, 180, 365].map((d) => d * DAY_SECONDS)];

const isDayUnit = (unit: WindowUnit): boolean => unit >= DAY_SECONDS;

/** Can the input show `seconds` in `unit` as a whole number within that unit's limits? */
function fits(seconds: number, unit: WindowUnit): boolean {
  if (seconds % unit !== 0) return false;
  const n = seconds / unit;
  if (!isDayUnit(unit)) return seconds <= DAY_SECONDS;
  return n >= 1 && n <= UNIT_MAX[unit];
}

/**
 * The unit the input shows the window in. The unit the analyst picked is kept while the window
 * is a whole number of it within its limits; otherwise the LARGEST unit that shows it exactly —
 * hours / minutes / seconds up to 24 h (86400 is "24 hours", as its preset reads), years /
 * months / days above. A long window that is not whole days (an older document) is shown
 * exactly, in seconds, so the engine's `window_whole_days` problem sits on a number that means
 * what it says.
 */
export function windowUnitFor(seconds: number, chosen?: WindowUnit): WindowUnit {
  if (!Number.isFinite(seconds)) return chosen ?? 1;
  if (chosen !== undefined && fits(seconds, chosen)) return chosen;
  const candidates = isLongWindow(seconds) ? WINDOW_UNITS.filter(isDayUnit) : WINDOW_UNITS.filter((u) => !isDayUnit(u));
  const exact = [...candidates].reverse().find((u) => fits(seconds, u));
  if (exact !== undefined) return exact;
  // over 366 days in whole days (the engine's `window_too_long`): still shown in days
  return isLongWindow(seconds) && seconds % DAY_SECONDS === 0 ? 86400 : 1;
}

/** The unit a preset chip shows its window in: "30 days" in days, "1 year" in years, a short one as `windowUnitFor` picks. */
export function presetUnit(seconds: number): WindowUnit {
  if (seconds === YEAR_SECONDS) return 31536000;
  return isLongWindow(seconds) ? 86400 : windowUnitFor(seconds);
}

/** The number the window shows for `seconds` in `unit` (`''` for a window not set). */
export function windowInputValue(seconds: number, unit: WindowUnit): number | '' {
  return Number.isFinite(seconds) ? seconds / unit : '';
}

/** The whole days of a long window (`null` for a window of at most 24 h). */
export function windowDays(seconds: number): number | null {
  return isLongWindow(seconds) ? Math.floor(seconds / DAY_SECONDS) : null;
}

// ---- "since": where a long window's history starts --------------------------------------

/** `2026-09-28` (a Bangkok calendar day) → `28/09/2026`, the console's date form; `null` for a day that does not exist. */
export function thaiDate(day: string): string | null {
  const p = parseThaiDay(day);
  if (!p) return null;
  return `${String(p.d).padStart(2, '0')}/${String(p.m).padStart(2, '0')}/${p.y}`;
}

/** One long-window measure's history: counting since a day, or nothing written yet. */
export type SinceNote = { kind: 'since'; day: string } | { kind: 'notYet' };

/** "counting since 28/09/2026" / "not counting yet — counts from the day this rule is released". */
export function sinceNoteText(note: SinceNote): string {
  if (note.kind === 'notYet') return t('longWindow.notYet');
  return t('longWindow.since', { date: thaiDate(note.day) ?? note.day });
}

/** A dry run's `LongWindowNote.since` as a note. */
export const sinceNoteOf = (since: string | null): SinceNote => (since === null ? { kind: 'notYet' } : { kind: 'since', day: since });

const SINCE_IN_DETAIL = /\bsince (\d{4}-\d{2}-\d{2})\b|\bno history yet\b/g;

/**
 * The "since" facts in a hit's evidence string (the decision detail). The engine writes
 * `since YYYY-MM-DD` or `no history yet` for every long-window measure it read — an expression
 * row may carry several (`big=3 [30d since 2026-09-28], small=1 [7d no history yet]; …`). One
 * note per distinct fact, in the order they appear; `[]` for a detail with none (every hit on a
 * window of at most 24 h).
 */
export function sinceNotesOf(detail: string | null | undefined): SinceNote[] {
  if (!detail) return [];
  const out: SinceNote[] = [];
  const seen = new Set<string>();
  for (const m of detail.matchAll(SINCE_IN_DETAIL)) {
    const note: SinceNote = m[1] ? { kind: 'since', day: m[1] } : { kind: 'notYet' };
    const key = note.kind === 'since' ? note.day : '';
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(note);
  }
  return out;
}

/** Where a dry run's long-window note is, in words: "PB-02", "PB-07 · Action 2 · Counter 1 (big)", with its window. */
export function longWindowPlace(note: LongWindowNote): string {
  const parts = [note.ruleId];
  if (note.row !== null) parts.push(t('place.branch', { n: note.row }));
  if (note.measure !== null) parts.push(t('place.measure', { n: note.measure }) + (note.name ? ` (${note.name})` : ''));
  parts.push(formatWindow(note.windowDays * DAY_SECONDS));
  return parts.join(' · ');
}

// ---- the window as two dropdowns: unit first, then the number (owner, 30 Sep) ---------------

/** The numbers offered for each unit. Days stop at 29 — 30 days and more read as months. */
export const WINDOW_CHOICES: Readonly<Record<WindowUnit, readonly number[]>> = {
  1: [10, 15, 20, 30, 45],
  60: [1, 2, 3, 5, 10, 15, 20, 30, 45],
  3600: [1, 2, 3, 4, 6, 8, 12, 18, 24],
  86400: Array.from({ length: 29 }, (_, i) => i + 1),
  2592000: Array.from({ length: 12 }, (_, i) => i + 1),
  31536000: [1],
};

/** The number a unit starts at when it is picked. */
export const UNIT_DEFAULT: Readonly<Record<WindowUnit, number>> = { 1: 30, 60: 10, 3600: 1, 86400: 7, 2592000: 1, 31536000: 1 };

/** The numbers for `unit`, plus the rule's own value when the list lacks it (so an existing rule still shows). */
export function windowChoices(unit: WindowUnit, seconds: number): number[] {
  const list = [...WINDOW_CHOICES[unit]];
  const own = windowInputValue(seconds, unit);
  if (typeof own === 'number' && Number.isInteger(own) && own > 0 && !list.includes(own)) list.push(own);
  return list.sort((a, b) => a - b);
}

